import { useMemo } from 'react';
import { useStudentAcademic, useStudentWellbeing } from '../../../api/hooks';
import { isWellbeingRaw } from '../../../api/types';
import { humanize } from '../../../lib/formatLabels';
import { Spinner, EmptyState, ErrorPanel } from '../../../components/ui';
import Box from './Box';
import { FaFileAlt } from '../../../components/ui/icons';
import { dayMonth, dayMonthTime, parseDate } from '../lib/format';

interface Props { studentId: number; }

function scoreColor(score: number | null): string {
  if (score === null) return 'text-muted';
  if (score < 50) return 'text-danger-ink';
  if (score < 70) return 'text-warning-ink';
  return 'text-success';
}

type Event = { key: string; at: number; when: string; node: JSX.Element };

/** Artboard `.tl`: a dotted timeline of the latest exam attempts and wellbeing check-ins. */
export default function RecentActivityCard({ studentId }: Props) {
  const q = useStudentAcademic(studentId);
  const wb = useStudentWellbeing(studentId);
  const events = useMemo<Event[]>(() => {
    const out: Event[] = [];
    for (const r of (q.data?.results ?? []).slice(0, 5)) {
      const d = parseDate(r.created);
      out.push({
        key: `r${r.id}`, at: d?.getTime() ?? 0, when: dayMonth(r.created),
        node: (
          <span className="flex items-baseline gap-2">
            <span className="min-w-0 flex-1">
              <span className="font-semibold text-ink">{r.exam_name ?? '—'}</span>
              <span className="ml-2 text-xs text-muted">{r.subject ?? '—'}{r.chapter ? ` · ${r.chapter}` : ''}</span>
            </span>
            <span className={`font-mono text-xs font-semibold ${scoreColor(r.score_percentage)}`}>
              {r.score_percentage !== null ? `${r.score_percentage.toFixed(0)}%` : '—'}
            </span>
          </span>
        ),
      });
    }
    if (wb.data && isWellbeingRaw(wb.data)) {
      wb.data.checkin_history.slice(0, 5).forEach((e, i) => {
        const state = String((e.payload as any)?.answer_slug ?? (e.payload as any)?.state ?? '');
        out.push({
          key: `c${i}`, at: parseDate(e.created_at)?.getTime() ?? 0, when: dayMonthTime(e.created_at),
          node: <span>Weekly check-in{state ? `: ${humanize(state).toLowerCase()}` : ''}</span>,
        });
      });
    }
    return out.sort((a, b) => b.at - a.at).slice(0, 5);
  }, [q.data, wb.data]);

  if (q.isLoading) {
    return (
      <Box title="Recent activity" help="recent_activity">
        <div className="flex justify-center py-4"><Spinner size="md" /></div>
      </Box>
    );
  }
  if (q.isError || !q.data) {
    return (
      <Box title="Recent activity" help="recent_activity"><ErrorPanel /></Box>
    );
  }
  return (
    <Box title="Recent activity" help="recent_activity">
      {events.length === 0 ? (
        <EmptyState compact icon={<FaFileAlt />} title="No exam attempts yet" />
      ) : (
        <ul className="text-sm">
          {events.map((e) => (
            <li key={e.key} className="relative pb-3.5 pl-[22px] last:pb-0">
              <span aria-hidden="true" className="absolute left-1 top-1.5 h-2 w-2 rounded-full bg-slate" />
              <span aria-hidden="true" className="absolute bottom-0 left-[7.5px] top-[18px] w-px bg-line-strong [li:last-child>&]:hidden" />
              <span className="block font-mono text-[11px] font-medium text-muted">{e.when}</span>
              {e.node}
            </li>
          ))}
        </ul>
      )}
    </Box>
  );
}
